<script lang="ts" setup>
const { t } = useI18n()
const {
  public: { siteUrl },
} = useRuntimeConfig()

useSeoMeta({
  title: t('privacy-policy'),
  description: t('this-policy-explains'),
  ogTitle: t('privacy-policy'),
  ogDescription: t('this-policy-explains'),
  ogImage: `${siteUrl}/images/head/snippet_screen.png`,
  ogImageWidth: '1200',
  ogImageHeight: '630',
  ogImageType: 'image/png',
  ogType: 'website',
  twitterTitle: 'Privacy Policy',
  twitterDescription: t('this-policy-explains'),
  twitterImage: `${siteUrl}/images/head/snippet_screen.png`,
  twitterCard: 'summary_large_image',
})

definePageMeta({
  layout: 'base',
})
</script>

<template>
  <div class="prose prose-invert mx-auto px-5 md:px-0">
    <h1>Belong Privacy Policy</h1>
    <p><strong>Dated: March 25, 2025 (Updated)</strong></p>
    <p>
      This policy explains what you can expect from us and what we need from you
      in relation to your personal data. Please read this carefully as this
      policy is legally binding when you use our Services.
    </p>
    <p>
      For the purpose of the relevant data protection regulations, Nomad
      Calendar's parent company Nomad Hub Inc is the "data controller" of your
      information. If you have any questions about how we protect or use your
      data, please email us at
      <a href="mailto:join@belong.net">join@belong.net</a>.
    </p>

    <h2>1. How do we protect your personal information</h2>
    <p>
      <strong>1.1</strong> We are serious about guarding the security of your
      personal information and use a secure server to store your personal
      information. All information you provide to us is stored on our secure
      servers. Any payment transactions will be encrypted using Transport Layer
      Security technology.
    </p>
    <p>
      <strong>1.2</strong> As you will know, the transmission of information via
      the internet is not completely secure. Although we will do our best to
      protect your personal data, we cannot guarantee the security of your data
      during transmission, and any transmission is at your own risk. Once we
      have received your information, we will use strict procedures and security
      features to try to prevent unauthorised access.
    </p>
    <p>
      <strong>1.3</strong> We restrict access of your personal information to
      those employees of Belong who have a business reason for knowing such
      information. We continuously educate and train our employees about the
      importance of confidentiality and privacy of customer information. We
      maintain physical, electronic and procedural safeguards that comply with
      the relevant laws and regulations to protect your personal information
      from unauthorised access.
    </p>

    <h2>2. Information we may collect from you</h2>
    <p>We may collect and use the following data about you:</p>
    <p><strong>2.1</strong> Information you give us.</p>
    <p>
      (a) You may give us information about you when you sign up to use our
      service, e.g. when you provide us with your personal details, such as your
      name and email address. This also includes information you provide through
      your continued use of our Services, participate in discussion boards or
      other social media functions on our Website or App, enter a competition,
      promotion or survey, and when you report a problem with our Services. The
      information you give us may include your name, address, e-mail address,
      phone number.
    </p>
    <p>
      (b) In providing the personal data of any individual (other than yourself)
      to us during your use of our Services, you promise that you have obtained
      consent from such individual to disclose his/her personal data to us, as
      well his/her consent to our collection, use and disclosure of such
      personal data, for the purposes set out in this Privacy Policy.
    </p>

    <p>
      <strong>2.2</strong> Information we collect about you. With regard to your
      use of our Services we may automatically collect the following
      information:
    </p>
    <p>
      (a) technical information, including the Internet protocol (IP) address
      used to connect your computer to the Internet, your login information,
      browser type and version, time zone setting, browser plug-in types and
      versions, operating system and platform;
    </p>
    <p>
      (b) information about your visit, including the full Uniform Resource
      Locators (URL) clickstream to, through and from our Website or App
      (including date and time); products you viewed or searched for; page
      response times, download errors, length of visits to certain pages, page
      interaction information (such as scrolling, clicks, and mouse-overs), and
      methods used to browse away from the page and any phone number used to
      call our Customer Support number.
    </p>

    <p>
      <strong>2.3</strong> Information we receive from other sources. We may
      receive information about you if you use any of the other websites we
      operate or the other services we provide. We are also working closely with
      third parties and may receive information about you from them.
    </p>
    <p>
      For example: advertising networks, analytics providers and search
      information providers may provide us with pseudonymised information about
      you, such as confirming how you found our website;
    </p>

    <p>
      <strong>2.4</strong> Information from social media networks. If you log in
      to our Services using your social media account (for example, Facebook or
      Google) we will receive relevant information that is necessary to enable
      our Services and authenticate you. The social media network will provide
      us with access to certain information that you have provided to them,
      including your name, profile image, e-mail address, and calendar data. We
      use such information, together with any other information you directly
      provide to us when registering or using our Services, to create your
      account and to communicate with you about the information, products and
      services that you request from us. You may also be able to specifically
      request that we have access to the contacts in your social media account
      so that you can send a referral link to your family and friends. We will
      use, disclose and store all of this information in accordance with this
      privacy policy.
    </p>

    <h2>3. Cookies</h2>
    <p>
      <strong>3.1</strong> Our Services use cookies to distinguish you from
      other users. This helps us to provide you with a good experience and also
      allows us to improve our Services.
    </p>

    <h2>4. Uses made of the information</h2>
    <p>We use your information in the following ways:</p>
    <ul>
      <li>
        (a) to carry out our obligations relating to your contract with us and
        to provide you with the information, products and services;
      </li>
      <li>
        (b) to comply with any applicable legal and/or regulatory requirements;
      </li>
      <li>(c) to notify you about changes to our Services;</li>
      <li>(d) as part of our efforts to keep our Services safe and secure;</li>
      <li>
        (e) to administer our Services and for internal operations, including
        troubleshooting, data analysis, testing, research, statistical and
        survey purposes;
      </li>
      <li>
        (f) to improve our Services and to ensure that they are presented in the
        most effective manner;
      </li>
      <li>
        (g) to measure or understand the effectiveness of advertising we serve
        and to deliver relevant advertising to you;
      </li>
      <li>
        (h) to allow you to participate in interactive features of our Services,
        when you choose to do so;
      </li>
      <li>
        (i) to provide you with information about other similar goods and
        services we offer;
      </li>
      <li>
        (j) to provide you, or permit selected third parties to provide you,
        with information about goods or services we feel may interest you; or
      </li>
      <li>
        (k) to combine information we receive from other sources with the
        information you give to us and information we collect about you. We may
        use this information and the combined information for the purposes set
        out above (depending on the types of information we receive).
      </li>
    </ul>

    <h2>5. Disclosure of your information</h2>
    <p>
      <strong>5.1</strong> We may share your information with selected third
      parties including:
    </p>
    <ul>
      <li>
        (a) affiliates, business partners, suppliers and sub-contractors for the
        performance and execution of any contract we enter into with them or
        you;
      </li>
      <li>
        (b) advertisers and advertising networks solely to select and serve
        relevant adverts to you and others;
      </li>
      <li>
        (c) analytics and search engine providers that assist us in the
        improvement and optimisation of our site; and
      </li>
      <li>(d) our group entities or subsidiaries.</li>
    </ul>

    <p>
      <strong>5.2</strong> We may disclose your personal information to third
      parties:
    </p>
    <ul>
      <li>
        (d) in the event that we sell or buy any business or assets, in which
        case we may disclose your personal data to the prospective seller or
        buyer of such business or assets;
      </li>
      <li>
        (e) if we are under a duty to disclose or share your personal data in
        order to comply with any legal obligation, or in order to enforce or
        apply our Terms of Service and other applicable agreements; or to
        protect the rights, property, or safety of Belong, our customers, or
        others. This includes exchanging information with other companies and
        organisations for the purposes of fraud protection and credit risk
        reduction;
      </li>
      <li>
        (f) to assist us in conducting or co-operating in investigations of
        illegal activity where we believe it is reasonable and appropriate to do
        so;
      </li>
      <li>(g) to prevent and detect fraud or crime;</li>
      <li>
        (h) in response to a subpoena, warrant, court order, or as otherwise
        required by law;
      </li>
      <li>(i) to develop customer relationships, services and systems.</li>
    </ul>

    <p>
      <strong>5.3</strong> We do not have a published list of all of the third
      parties with whom we share your data with, as this would be heavily
      dependant on your specific use of our Services. However, if you would like
      further information about who we have shared your data with, or to be
      provided with a list specific to You, you can request this by writing to
      <a href="mailto:join@belong.net">join@belong.net</a>.
    </p>

    <h2>6. Where we store your personal data</h2>
    <p>
      <strong>6.1</strong> The data that we collect from you may be transferred
      to, and stored at, a destination outside the European Economic Area
      ("EEA"). It may also be processed by staff operating outside the EEA who
      work for us or for one of our suppliers. Such staff maybe engaged in,
      among other things, the fulfilment of your payment order, the processing
      of your payment details and the provision of support services. By
      submitting your personal data, you agree to this transfer, storing or
      processing. We will take all steps reasonably necessary to ensure that
      your data is treated securely and in accordance with this privacy policy.
    </p>

    <h2>7. Sharing your data outside of the EEA</h2>
    <p>
      <strong>7.1</strong> In order to provide our Services to you, it is
      sometimes necessary for us to transfer your data to the third parties
      outlined in section 5.1 that are based outside of the European Economic
      Area. In these cases, we ensure that both ourselves and our partners take
      adequate and appropriate technical, physical and organisational security
      measures to protect your data. We also ensure we have appropriate
      contractual protections in place with these third parties.
    </p>

    <h2>8. Profiling</h2>
    <p>
      <strong>8.1</strong> We may use some instances of your data in order to
      customise our Services and the information we provide to you, and to
      address your needs – such as your country or travel history.
    </p>

    <h2>9. Data Retention</h2>
    <p>
      <strong>9.1</strong> Your data is only accessed internally on a need to
      know basis, and it will only be accessed or processed if absolutely
      necessary.
    </p>
    <p>
      <strong>9.2</strong> We will always delete data that is no longer needed.
    </p>

    <h2>10. Your rights</h2>
    <p>
      <strong>10.1</strong> You have the right to ask us not to contact you for
      marketing purposes by contacting us at
      <a href="mailto:join@belong.net">join@belong.net</a>
    </p>
    <p>
      <strong>10.2</strong> You have the right to correct any personal
      information we hold on you that is inaccurate, incorrect, or out of date.
    </p>
    <p>
      <strong>10.3</strong> You have the right to ask us to delete your data
      when it is no longer necessary.
    </p>

    <p>
      <strong>10.4</strong> Our Services may, from time to time, contain links
      to and from the websites of our partner networks, advertisers and
      affiliates. If you follow a link to any of these websites, please note
      that these websites have their own privacy policies and that we do not
      accept any responsibility for them. Please check these policies before you
      submit any personal data to these websites.
    </p>

    <h2>11. Access to information</h2>
    <p>
      <strong>11.1</strong> Subject to applicable laws, you may have the right
      to access information we hold about you. Your right of access can be
      exercised in accordance with the relevant data protection legislation.
    </p>

    <h2>12. Child Safety Standards</h2>
    <p>
      <strong>12.1</strong> Standards Against Child Sexual Abuse and
      Exploitation (CSAE) Belong has published standards against child sexual
      abuse and exploitation. We have zero tolerance for any content, behavior,
      or actions that exploit, abuse, or harm minors. We utilize both automated
      systems and human review to detect and remove such content. Any account
      found to be involved in such activities will be immediately terminated and
      reported to the appropriate authorities.
    </p>

    <p>
      <strong>12.2</strong> In-App Feedback Mechanism We provide an in-app
      mechanism for users to report concerns about child safety or inappropriate
      content. This reporting tool is prominently displayed and accessible
      throughout our application. All reports are treated with the highest
      priority and investigated promptly.
    </p>

    <p>
      <strong>12.3</strong> Addressing Child Sexual Abuse Material (CSAM) We
      actively monitor for and remove any child sexual abuse material. We employ
      industry-standard content moderation tools and work with relevant
      authorities and organizations to prevent the spread of such content. We
      also participate in industry initiatives designed to combat CSAM.
    </p>

    <p>
      <strong>12.4</strong> Compliance with Child Safety Laws Belong complies
      with all applicable child safety laws in the jurisdictions where our
      services are available. This includes but is not limited to COPPA
      (Children's Online Privacy Protection Act) in the United States and
      equivalent regulations in other countries. We implement age verification
      measures where required and maintain appropriate data protection standards
      for any information collected from minors.
    </p>

    <p>
      <strong>12.5</strong> Child Safety Point of Contact We have designated a
      Child Safety Officer who serves as the primary point of contact for all
      child safety concerns. This individual can be reached at
      <a href="mailto:childsafety@belong.net">childsafety@belong.net</a>. For
      urgent matters related to child safety, please also contact your local
      authorities immediately.
    </p>

    <h2>13. Changes to our privacy policy</h2>
    <p>
      <strong>13.1</strong> Any changes we may make to our privacy policy will
      be posted on this page and, where appropriate, notified to you by e-mail.
      Please check back frequently to see any updates or changes to our privacy
      policy.
    </p>

    <h2>14. Contact</h2>
    <p>
      <strong>14.1</strong> Questions, comments and requests regarding this
      privacy policy are welcomed and should be addressed to
      <a href="mailto:join@belong.net">join@belong.net</a>.
    </p>
  </div>
</template>
